import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import {
  TrendingUp,
  TrendingDown,
  Mail,
  MessageSquare,
  Phone,
  Users,
  DollarSign,
  Eye,
  MousePointer,
  CheckCircle,
  BarChart3,
  Calendar,
  Filter,
  Download,
  Target,
  Award
} from 'lucide-react';
import { getDateRange } from '../lib/google-ads-client';
import toast from 'react-hot-toast';

interface CampaignStats {
  id: string;
  name: string;
  status: string;
  sent_count: number;
  delivered_count: number;
  opened_count: number;
  clicked_count: number;
  converted_count: number;
  revenue_cents: number;
  cost_cents: number;
}

interface DailyAnalytics {
  date: string;
  emails_sent: number;
  emails_opened: number;
  emails_clicked: number;
  sms_sent: number;
  calls_made: number;
  revenue_generated_cents: number;
}

export default function AnalyticsPage() {
  const [campaigns, setCampaigns] = useState<CampaignStats[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all');
  const [googleAdsPPCMetrics, setGoogleAdsPPCMetrics] = useState<any>(null);

  useEffect(() => {
    loadAnalytics();
    loadGoogleAdsPPCMetrics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      startDate.setDate(startDate.getDate() - days);

      // Load campaign stats
      const { data: campaignData, error: campaignError } = await supabase
        .from('marketing_campaigns')
        .select('id, name, status, sent_count, delivered_count, opened_count, clicked_count, converted_count, revenue_cents, cost_cents')
        .order('sent_count', { ascending: false })
        .limit(10);

      if (campaignError) throw campaignError;

      // Load daily analytics
      const { data: dailyData, error: dailyError } = await supabase
        .from('marketing_analytics_daily')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (dailyError) throw dailyError;

      setCampaigns(campaignData || []);
      setDailyStats(dailyData || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleAdsPPCMetrics = async () => {
    try {
      const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
      const range = getDateRange(days);
      
      const { data } = await supabase
        .from('google_ads_campaign_performance')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate);

      if (data && data.length > 0) {
        const totals = data.reduce((acc, row) => ({
          impressions: acc.impressions + (row.impressions || 0),
          clicks: acc.clicks + (row.clicks || 0),
          cost: acc.cost + ((row.cost_micros || 0) / 1000000),
          conversions: acc.conversions + (row.conversions || 0),
          conversionValue: acc.conversionValue + (row.conversion_value || 0)
        }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionValue: 0 });

        const ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
        const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
        const conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
        const roas = totals.cost > 0 ? totals.conversionValue / totals.cost : 0;
        const costPerConversion = totals.conversions > 0 ? totals.cost / totals.conversions : 0;

        setGoogleAdsPPCMetrics({
          ...totals,
          ctr,
          avgCpc,
          conversionRate,
          roas,
          costPerConversion,
          activeCampaigns: new Set(data.map(r => r.campaign_id)).size
        });
      }
    } catch (error) {
      console.error('Error loading Google Ads PPC metrics:', error);
    }
  };

  // Calculate overall metrics
  const calculateMetrics = () => {
    const filteredCampaigns = selectedCampaign === 'all' 
      ? campaigns 
      : campaigns.filter(c => c.id === selectedCampaign);

    const totalSent = filteredCampaigns.reduce((sum, c) => sum + c.sent_count, 0);
    const totalDelivered = filteredCampaigns.reduce((sum, c) => sum + c.delivered_count, 0);
    const totalOpened = filteredCampaigns.reduce((sum, c) => sum + c.opened_count, 0);
    const totalClicked = filteredCampaigns.reduce((sum, c) => sum + c.clicked_count, 0);
    const totalConverted = filteredCampaigns.reduce((sum, c) => sum + c.converted_count, 0);
    const totalRevenue = filteredCampaigns.reduce((sum, c) => sum + c.revenue_cents, 0) / 100;
    const totalCost = filteredCampaigns.reduce((sum, c) => sum + c.cost_cents, 0) / 100;

    const openRate = totalDelivered > 0 ? ((totalOpened / totalDelivered) * 100).toFixed(1) : '0.0';
    const clickRate = totalOpened > 0 ? ((totalClicked / totalOpened) * 100).toFixed(1) : '0.0';
    const conversionRate = totalSent > 0 ? ((totalConverted / totalSent) * 100).toFixed(1) : '0.0';
    const roi = totalCost > 0 ? (((totalRevenue - totalCost) / totalCost) * 100).toFixed(1) : '0.0';

    return {
      totalSent,
      totalDelivered,
      totalOpened,
      totalClicked,
      totalConverted,
      totalRevenue,
      totalCost,
      openRate: parseFloat(openRate),
      clickRate: parseFloat(clickRate),
      conversionRate: parseFloat(conversionRate),
      roi: parseFloat(roi),
    };
  };

  const metrics = calculateMetrics();

  // Calculate channel breakdown from daily stats
  const channelStats = dailyStats.reduce(
    (acc, day) => ({
      emails: acc.emails + day.emails_sent,
      sms: acc.sms + day.sms_sent,
      calls: acc.calls + day.calls_made,
    }),
    { emails: 0, sms: 0, calls: 0 }
  );

  const totalChannelMessages = channelStats.emails + channelStats.sms + channelStats.calls;

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading analytics...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 relative overflow-hidden">
      {/* Page Background Elements */}
      <div className="absolute inset-0 pointer-events-none select-none -z-10">
        <div className="absolute top-10 right-10 w-64 h-64 bg-blue-500/3 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-indigo-500/2 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-purple-500/2 rounded-full blur-3xl animate-pulse"></div>
      </div>

      {/* Premium Page Header */}
      <div className="mb-12 text-center">
        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-lg rounded-2xl px-6 py-4 border border-white/20 shadow-2xl mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div className="text-left">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
              Campaign Analytics
            </h1>
            <p className="text-gray-600 font-medium">Track performance and measure ROI across all channels</p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as '7d' | '30d' | '90d')}
            className="px-6 py-3 bg-white/95 backdrop-blur-sm border border-white/20 rounded-xl shadow-lg text-gray-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:shadow-xl"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
          <button
            onClick={() => {
              toast.success('Export functionality coming soon! CSV download will be implemented.', {
                duration: 3000,
                icon: '📊'
              });
            }}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
          >
            <Download className="w-5 h-5" />
            Export Report
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] hover:bg-white/100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-semibold">+12.5%</span>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Messages Sent</h3>
          <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">{metrics.totalSent.toLocaleString()}</p>
          <p className="text-sm text-gray-500">
            Delivered: {metrics.totalDelivered.toLocaleString()}
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] hover:bg-white/100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-500/10 rounded-xl">
              <Eye className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-semibold">+8.3%</span>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Open Rate</h3>
          <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">{metrics.openRate}%</p>
          <p className="text-sm text-gray-500">
            {metrics.totalOpened.toLocaleString()} opens
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] hover:bg-white/100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-indigo-500/10 rounded-xl">
              <MousePointer className="w-6 h-6 text-indigo-600" />
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-semibold">+6.1%</span>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Click Rate</h3>
          <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">{metrics.clickRate}%</p>
          <p className="text-sm text-gray-500">
            {metrics.totalClicked.toLocaleString()} clicks
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-3xl p-6 shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] hover:bg-white/100">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-500/10 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex items-center gap-2 text-green-600">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm font-semibold">+15.2%</span>
            </div>
          </div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Conversion Rate</h3>
          <p className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">{metrics.conversionRate}%</p>
          <p className="text-sm text-gray-500">
            {metrics.totalConverted.toLocaleString()} conversions
          </p>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Revenue Generated</h3>
            <DollarSign className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-3xl font-bold text-green-600">${metrics.totalRevenue.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-2">
            From {metrics.totalConverted.toLocaleString()} conversions
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">Marketing Spend</h3>
            <DollarSign className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">${metrics.totalCost.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-2">
            Campaign costs and expenses
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-600">ROI</h3>
            <BarChart3 className="w-5 h-5 text-blue-500" />
          </div>
          <p className={`text-3xl font-bold ${metrics.roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {metrics.roi > 0 ? '+' : ''}{metrics.roi}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            ${(metrics.totalRevenue - metrics.totalCost).toLocaleString()} profit
          </p>
        </div>
      </div>

      {/* Channel Breakdown */}
      <div className="card mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Channel Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Email</span>
                <span className="text-sm font-bold text-gray-900">
                  {totalChannelMessages > 0 ? ((channelStats.emails / totalChannelMessages) * 100).toFixed(0) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{
                    width: `${totalChannelMessages > 0 ? (channelStats.emails / totalChannelMessages) * 100 : 0}%`,
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">{channelStats.emails.toLocaleString()} sent</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <MessageSquare className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">SMS</span>
                <span className="text-sm font-bold text-gray-900">
                  {totalChannelMessages > 0 ? ((channelStats.sms / totalChannelMessages) * 100).toFixed(0) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{
                    width: `${totalChannelMessages > 0 ? (channelStats.sms / totalChannelMessages) * 100 : 0}%`,
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">{channelStats.sms.toLocaleString()} sent</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <Phone className="w-8 h-8 text-purple-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">AI Calls</span>
                <span className="text-sm font-bold text-gray-900">
                  {totalChannelMessages > 0 ? ((channelStats.calls / totalChannelMessages) * 100).toFixed(0) : 0}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{
                    width: `${totalChannelMessages > 0 ? (channelStats.calls / totalChannelMessages) * 100 : 0}%`,
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-1">{channelStats.calls.toLocaleString()} made</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Performing Campaigns */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Top Performing Campaigns</h2>
          <select
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
            className="input-field"
          >
            <option value="all">All Campaigns</option>
            {(campaigns || []).map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name}
              </option>
            ))}
          </select>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-8">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No campaign data available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-900">Campaign</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Sent</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Opens</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Clicks</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Conversions</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">Revenue</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-900">ROI</th>
                </tr>
              </thead>
              <tbody>
                {(campaigns || []).map((campaign) => {
                  const openRate = campaign.delivered_count > 0
                    ? ((campaign.opened_count / campaign.delivered_count) * 100).toFixed(1)
                    : '0.0';
                  const clickRate = campaign.opened_count > 0
                    ? ((campaign.clicked_count / campaign.opened_count) * 100).toFixed(1)
                    : '0.0';
                  const convRate = campaign.sent_count > 0
                    ? ((campaign.converted_count / campaign.sent_count) * 100).toFixed(1)
                    : '0.0';
                  const roi = campaign.cost_cents > 0
                    ? (((campaign.revenue_cents - campaign.cost_cents) / campaign.cost_cents) * 100).toFixed(0)
                    : '0';

                  return (
                    <tr key={campaign.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{campaign.name}</p>
                          <p className="text-sm text-gray-500">{campaign.status}</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 text-gray-900">
                        {campaign.sent_count.toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-4">
                        <div>
                          <p className="text-gray-900">{campaign.opened_count.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{openRate}%</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <div>
                          <p className="text-gray-900">{campaign.clicked_count.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{clickRate}%</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <div>
                          <p className="text-gray-900">{campaign.converted_count.toLocaleString()}</p>
                          <p className="text-xs text-gray-500">{convRate}%</p>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 text-green-600 font-semibold">
                        ${(campaign.revenue_cents / 100).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${parseInt(roi) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseInt(roi) > 0 ? '+' : ''}{roi}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Google Ads PPC Metrics Section */}
      {googleAdsPPCMetrics && (
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Paid Search Performance (Google Ads)</h2>
                <p className="text-sm text-gray-600">Campaign metrics for selected time period</p>
              </div>
            </div>
            <a
              href="/google-ads"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium inline-flex items-center gap-2"
            >
              View All Tools
              <Target className="w-4 h-4" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Eye className="w-6 h-6 text-gray-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsPPCMetrics.impressions.toLocaleString()}</div>
              <div className="text-xs text-gray-600">Impressions</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <MousePointer className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsPPCMetrics.clicks.toLocaleString()}</div>
              <div className="text-xs text-gray-600">Clicks</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <BarChart3 className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsPPCMetrics.ctr.toFixed(2)}%</div>
              <div className="text-xs text-gray-600">CTR</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <DollarSign className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">${googleAdsPPCMetrics.avgCpc.toFixed(2)}</div>
              <div className="text-xs text-gray-600">Avg CPC</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsPPCMetrics.conversions}</div>
              <div className="text-xs text-gray-600">Conversions</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Award className="w-6 h-6 text-orange-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-gray-900">{googleAdsPPCMetrics.roas.toFixed(2)}x</div>
              <div className="text-xs text-gray-600">ROAS</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Total Ad Spend</div>
              <div className="text-2xl font-bold text-gray-900">${googleAdsPPCMetrics.cost.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">Across {googleAdsPPCMetrics.activeCampaigns} campaigns</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Conversion Value</div>
              <div className="text-2xl font-bold text-gray-900">${googleAdsPPCMetrics.conversionValue.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">From {googleAdsPPCMetrics.conversions} conversions</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Cost Per Conversion</div>
              <div className="text-2xl font-bold text-gray-900">${googleAdsPPCMetrics.costPerConversion.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">Avg CPA</div>
            </div>
          </div>
        </div>
      )}

      {/* Daily Trend Chart Placeholder */}
      <div className="card">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Performance Trend</h2>
        <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="text-center">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">Chart visualization coming soon</p>
            <p className="text-sm text-gray-400 mt-1">
              Integration with charting library (Chart.js or Recharts) needed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
